import React, {useEffect, useRef} from "react";
import {Layout, Typography, Button, Row, Col, Divider} from "antd";
import {useNavigate} from "react-router-dom";
import Header from "../../../components/molecules/Header/component";
import {useIsMobile} from "../../../utils/helpers";
import Footer from "../../../components/molecules/Footer/component";
import Seo from '../../../components/atoms/Seo/component';
import {trackEvent} from '../../../analytics/posthog';
import {useTrackOnView} from '../../../analytics/useTrackOnView';

const {Content} = Layout;
const {Title, Paragraph, Text} = Typography;

const usd = (n: number) =>
    new Intl.NumberFormat("en-US", {style: "currency", currency: "USD"}).format(n);

const plans = [
    {
        name: "Advertiser",
        monthly: 95,
        periods: [
            {label: "Monthly", price: 95},
            {label: "Quarterly", price: 270, savePct: 5.26},
            {label: "Half-year", price: 510, savePct: 10.53},
            {label: "Yearly", price: 960, savePct: 15.79},
        ],
    },
    {
        name: "Promoter",
        monthly: 45,
        periods: [
            {label: "Monthly", price: 45},
            {label: "Quarterly", price: 128, savePct: 5.19},
            {label: "Half-year", price: 243, savePct: 10.0},
            {label: "Yearly", price: 459, savePct: 15.0},
        ],
    },
    {
        name: "Partner",
        monthly: 175,
        periods: [
            {label: "Monthly", price: 175},
            {label: "Quarterly", price: 498, savePct: 5.14},
            {label: "Half-year", price: 945, savePct: 10.0},
            {label: "Yearly", price: 1782, savePct: 15.14},
        ],
    },
];

const PricingPage: React.FC = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const advertiserPlanRef = useRef<HTMLDivElement>(null);
    const promoterPlanRef = useRef<HTMLDivElement>(null);
    const partnerPlanRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        trackEvent('public_pricing_page_viewed');
    }, []);

    useTrackOnView(advertiserPlanRef, 'public_pricing_advertiser_plan_viewed', {
        selected_role: 'advertiser',
        plan_name: 'Advertiser'
    });
    useTrackOnView(promoterPlanRef, 'public_pricing_promoter_plan_viewed', {
        selected_role: 'promoter',
        plan_name: 'Promoter'
    });
    useTrackOnView(partnerPlanRef, 'public_pricing_partner_plan_viewed', {
        selected_role: 'partner',
        plan_name: 'Partner'
    });

    return (
        <div>
            <Seo
                title="Promotify Pricing"
                description="Compare Promotify advertiser, promoter, and partner plans for campaign promotion, verified click tracking, referrals, and payouts."
                canonicalPath="/pricing"
                structuredData={{
                    '@context': 'https://schema.org',
                    '@type': 'FAQPage',
                    mainEntity: [
                        {
                            '@type': 'Question',
                            name: 'What Promotify plans are available?',
                            acceptedAnswer: {
                                '@type': 'Answer',
                                text: 'Promotify offers advertiser, promoter, and partner plans for performance-based campaign promotion, verified click tracking, and referral earnings.'
                            }
                        },
                        {
                            '@type': 'Question',
                            name: 'Do new Promotify accounts include a trial?',
                            acceptedAnswer: {
                                '@type': 'Answer',
                                text: 'New accounts include a one-month free trial before the selected subscription renews automatically until cancelled.'
                            }
                        }
                    ]
                }}
            />
            <Header/>
            <Content style={{maxWidth: 900, margin: "0 auto", padding: "32px 16px 48px"}}>
                <article>
                    <header>
                        <Title level={2}>Pricing & Plans</Title>
                        <Paragraph>
                            Promotify offers flexible subscription plans depending on how you want to use the platform.
                            You can explore the platform for free and activate a plan when you&apos;re ready to run
                            campaigns or start earning.
                        </Paragraph>
                    </header>

                    <section style={{marginTop: 24}}>
                        <Title level={3}>Advertiser</Title>
                        <Paragraph>
                            Designed for businesses that want to promote their product or service using
                            performance-based
                            marketing. Advertisers create campaigns, define click budgets and pricing, and track results
                            in real time.
                        </Paragraph>
                        <Paragraph>
                            Campaigns are funded at checkout and advertisers only pay for verified clicks according to
                            their configuration.
                        </Paragraph>
                    </section>

                    <section style={{marginTop: 20}}>
                        <Title level={3}>Promoter</Title>
                        <Paragraph>
                            Promoters browse available campaigns and share unique referral links. Earnings are generated
                            when verified clicks are driven to advertiser campaigns.
                        </Paragraph>
                        <Paragraph>
                            The dashboard provides performance tracking, link analytics, and payout history after
                            verification.
                        </Paragraph>
                    </section>

                    <section style={{marginTop: 20}}>
                        <Title level={3}>Partner</Title>
                        <Paragraph>
                            Partners expand on the promoter role by building a referral network. In addition to earning
                            from their own activity, partners receive a percentage of subscription revenue from
                            recruited users and commissions from referred advertisers.
                        </Paragraph>
                        <Paragraph>
                            Earnings are verified and released after settlement and fraud review processes.
                        </Paragraph>
                    </section>

                    <section style={{marginTop: 32}}>
                        <Title level={3}>Price breakdown</Title>

                        <Row gutter={[32, 32]} style={{marginTop: 12}}>
                            {plans.map((plan) => (
                                <Col xs={24} md={8} key={plan.name}>
                                    <div
                                        ref={
                                            plan.name === 'Advertiser'
                                                ? advertiserPlanRef
                                                : plan.name === 'Promoter'
                                                    ? promoterPlanRef
                                                    : partnerPlanRef
                                        }
                                        style={{textAlign: "center"}}
                                    >
                                        <Title level={4} style={{color: "#1677ff", marginBottom: 4}}>
                                            {plan.name}
                                        </Title>

                                        <Text type="secondary">{usd(plan.monthly)} / month</Text>

                                        <Divider/>

                                        <div style={{display: "grid", gap: 10}}>
                                            {plan.periods.map((p) => (
                                                <div key={p.label}
                                                     style={{display: "flex", justifyContent: "space-between"}}>
                                                    <span>{p.label}</span>
                                                    <span>
                            <strong>{usd(p.price)}</strong>
                                                        {p.savePct && (
                                                            <span style={{color: "#16a34a", marginLeft: 6}}>
                                ({p.savePct}% off)
                              </span>
                                                        )}
                          </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    <section style={{marginTop: 32}}>
                        <Title level={3}>Trial & Billing</Title>
                        <Paragraph>
                            All new accounts include a one-month free trial. After the trial period, the selected
                            subscription is charged immediately and renews automatically until cancelled from the
                            billing portal.
                        </Paragraph>
                    </section>

                    <section style={{marginTop: 24}}>
                        <Title level={3}>Payment failures & account status</Title>
                        <Paragraph>
                            If a subscription renewal charge cannot be successfully processed, the account will be
                            marked as inactive until payment is resolved. While the account is inactive, certain
                            platform
                            features will be limited or paused.
                        </Paragraph>

                        <Paragraph>
                            <Text strong>Advertisers</Text>
                            <br/>
                            Active campaigns will be paused and campaign links will stop accepting traffic. Promoters
                            and partners linked to those campaigns will be notified and the campaigns will become
                            unavailable until payment is completed. Campaign activity resumes automatically once payment
                            is successful.
                        </Paragraph>

                        <Paragraph>
                            <Text strong>Promoters</Text>
                            <br/>
                            Referral links become inactive and new eligible clicks are not recorded while the account is
                            inactive. Existing earnings remain in the account and are not removed.
                        </Paragraph>

                        <Paragraph>
                            <Text strong>Partners</Text>
                            <br/>
                            Referral tracking and recruitment features are suspended until the partner subscription is
                            restored.
                        </Paragraph>

                        <Paragraph>
                            Users will receive in-app notifications and email alerts when a payment failure occurs. If
                            payment is not resolved within an extended period, Promotify may restrict access or close
                            the
                            account in accordance with our Terms.
                        </Paragraph>

                        <Paragraph>
                            Short notice for users: <em>If your payment fails, your account features will pause
                            automatically until you update your payment method.</em>
                        </Paragraph>
                    </section>

                    <footer
                        style={{
                            marginTop: 24,
                            display: "flex",
                            gap: 8,
                            flexDirection: isMobile ? "column" : "row",
                            justifyContent: isMobile ? "stretch" : "flex-end"
                        }}
                    >
                        <Button
                            type="default"
                            onClick={() => {
                                trackEvent('public_pricing_contact_support_clicked', {
                                    destination: '/contact'
                                });
                                navigate("/contact");
                            }}
                            block={isMobile}
                            style={{width: isMobile ? "100%" : "auto"}}
                        >
                            Contact support
                        </Button>
                    </footer>
                </article>
            </Content>
            <Footer/>
        </div>
    );
};

export default PricingPage;
